import { Injectable } from 'angular-ts-decorators';

import { map, isEmpty, pick } from 'lodash';
import { Domain } from '@shared/app/scripts/services/domain';
import { Sentry } from '@shared/app/scripts/services/sentry';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';

/**
 * # spynlBase
 * Service in the sharedModule. It's a reusabe service for communicating with
 * spynl collections.
 *
 * ## Using the service
 * Service is not intended for direct usage. It's checking the *location* property at `this`,
 * and the only safe way to set it is in a service that inherits prototypically form this one.
 *
 * ## Example
 * Please check {@link sharedModule.transaction transaction service} as a usage example.
 */
export class SpynlBase<T extends object> {
  protected location: string;

  constructor(
    protected $http: ng.IHttpService,
    protected $q: ng.IQService,
    protected sentry: Sentry,
    protected domain: Domain,
    protected config: IConfig,
    protected standardErrorParser: IStandardErrorParser,
    protected TConstructor: new (...args: any[]) => T,
  ) {}

  /**
   * Get the url for the collection
   * @param {string} prefix modifiable part of the route, before the resource
   * name. Used mostly for /tenants/<id>
   * @returns {string} collection url
   */
  getLocation(prefix?: string): string {
    prefix = angular.isString(prefix) ? prefix : '';

    if (angular.isString(this.location)) {
      return this.baseUrl() + prefix + this.location;
    }

    throw new Error('Attempt to access collection with undefined location');
  }

  /**
   * Get the url for the backend
   * @returns {string} spynl url
   */
  baseUrl(): string {
    return '//' + this.domain.sibling(this.config.spynlSubdomain);
  }

  /**
   * Return entities available for the tenant. Data is paginated by default due to large data sets.
   * @param {number} page
   * page counter. Only natural numbers including 0 are valid. Default 0 - first page.
   * @param {number} limit
   * page size. Default 100
   * @param {string[]} fields
   * Fields the result should return
   * @param {object[]} sort
   * Optional param to determine sorting order returned.
   * Available directions: 1 => ascending, -1 => descending
   * Example : [{field: 'lorem', direction: 1}]
   * @returns {Promise<T>} resolve with data array, or fails with error description.
   */
  query(page?: number, limit?: number, fields?: string[], sort?: object[]) {
    return this.filter(undefined, page, limit, fields, sort, undefined);
  }

  /**
   * Return entities available for the tenant & matching by filter. Data is
   * paginated by default due to large data sets.
   * @param {object} filter
   * filter object, as being sent to {@link
   * http://docs.mongodb.org/manual/reference/method/db.collection.find/
   * mongo's find function}.
   * @param {int} page
   * page counter. Only natural numbers including 0 are valid. Default 0 - first page.
   * @param {int} limit
   * page size. Default 100
   * @param {array} fields
   * Fields the result should return
   * @param {array} sort
   * Fields it should sort on ex: [['name', 1]] or [['name', -1]] for ascending or descending
   * @param {string} tenantId
   * Optional tenantId
   * @returns {Promise<T>} resolve with data array, or fails with error description.
   */
  filter(
    filter: object,
    page: number = 0,
    limit: number = 100,
    fields?: string[],
    sort?: any[],
    tenantId?: string,
  ) {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .post(
        this.getLocation(tenantSection) + '/get',
        {
          limit,
          skip: page * limit,
          filter,
          fields,
          sort,
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((x) => x, this.standardErrorParser)
      .then(
        (response: any) => {
          return map(
            response.data.data,
            (value) => new this.TConstructor(value),
          );
          // TODO CORE-440 move to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * Get the collection entity by id.
   * @param {string} id
   * entity id
   * @param {string} tenantId
   * Optional tenantId
   * @returns {Promise<T>} resolve with entity object, or fails with error description.
   */
  get(id: string, tenantId?: string) {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    const location = this.getLocation(tenantSection);

    return this.$http
      .post(
        location + '/get',
        { filter: { _id: id } },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((x) => x, this.standardErrorParser)
      .then(
        (response: any) => {
          const dataArray = response.data.data;

          switch (dataArray.length) {
            case 1:
              return new this.TConstructor(dataArray[0]);
            case 0:
              return this.$q.reject('Not found');
            default:
              this.sentry.captureMessage(
                'Found more then one ' + location + ' entity with id = ' + id,
              );
              return this.$q.reject(
                'Error: found more then 1 entity with the same _id',
              );
          }
          // TODO CORE-440 move to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * Matching filter. When non specified, all entities are counted.
   * @param {object} filter
   * filter object
   * @param {string} tenantId
   * Optional tenantId
   * @returns {httpPromise} resolve with entities count, or fails with error description.
   */
  count(filter?: object, tenantId?: string) {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return (
      this.$http
        .post(
          this.getLocation(tenantSection) + '/count',
          {
            filter: filter,
          },
          {
            // done on config level for v7; setting for v6
            withCredentials: true,
          },
        )
        // TODO CORE-440 move error to catch
        .then(
          (response: any) => response.data.count,
          (error) => this.$q.reject(error.data.message),
        )
    );
  }

  /**
   * Try to run update operation defined in updateOperation.
   * @param {string} id
   * Id of the object to be updated
   * @param {object} updateOperation
   * Object with the object describing the update operation. Available options
   * as described in the mongo doc: {@link
   * https://docs.mongodb.org/v2.4/reference/operator/update-field/ mongo
   * update operators}
   * @param {string} tenantId
   * Optional tenantId
   * @returns {promise} it will resolve with success message, or fail with error description
   */
  update(id: string, updateOperation: object, tenantId?: string) {
    // TODO move to data object itself
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    if (!angular.isString(id)) {
      return this.$q.reject('The entity id is undefined, not able to save it');
    }

    return this.$http
      .post(
        this.getLocation(tenantSection) + '/edit',
        {
          filter: {
            _id: id,
          },
          data: updateOperation,
          // TODO CORE-440 move error to catch
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((x) => x, this.standardErrorParser)
      .then(
        (response: any) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * A method which allows the adding of a single document to the spynl API
   * @param {object} document
   * The document to be added
   * @param {string} tenantId
   * Optional tenantId
   * @returns {promise} it will resolve with success message, or fail with error description
   */
  add(document: T, tenantId?: string) {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    if (!angular.isObject(document) || angular.isArray(document)) {
      return this.$q.reject(
        'The type of document is not supported. Must be an Object.',
      );
    }

    return this.$http
      .post(
        this.getLocation(tenantSection) + '/add',
        {
          data: document,
          // TODO CORE-440 move error to catch
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((x) => x, this.standardErrorParser)
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * A method which allows the editing of a multiple documents to the spynl API
   * @param {object} filter
   * The filter to locate a document or documents to be edited
   * @param {object} updateOperation
   * The object used to edit the existing documents. Properties from this object will be
   * $set or $unset from the existing documents depending on the update operator to be used.
   * Allowed operators:
   * * $set - replaces the value of a field with the specified value.
   * * $unset - deletes a particular field.
   * * $push - appends a specified value to an array.
   * * $pop - removes the first or last element of an array. Pass $pop a value of -1 to remove.
   * the first element of an array and 1 to remove the last element in an array.
   * * $pull - removes from an existing array all instances of a value or values
   * that match a specified condition.
   * * $addToSet - adds a value to an array unless the value is already present, in which case
   * $addToSet does nothing to that array.
   * @param {string} tenantId
   * Optional tenantId
   * @returns {promise} it will resolve with success message, or fail with error description
   */
  edit(filter: object, updateOperation: object, tenantId: string) {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }
    const allowedOperators = [
      '$set',
      '$unset',
      '$push',
      '$pop',
      '$pull',
      '$addToSet',
    ];

    if (!angular.isObject(filter)) {
      return this.$q.reject('The filter must be of type Object.');
    }

    if (!angular.isObject(updateOperation)) {
      return this.$q.reject('The updateOperation must be of type Object.');
    }

    // Clense update operation of unallowed operators
    updateOperation = pick(updateOperation, allowedOperators);

    if (isEmpty(updateOperation)) {
      return this.$q.reject(
        'No supported update operators found. Supported operators: ' +
          allowedOperators.join(', '),
      );
    }

    return this.$http
      .post(
        this.getLocation(tenantSection) + '/edit',
        {
          filter: filter,
          data: updateOperation,
          // TODO CORE-440 move error to catch
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((x) => x, this.standardErrorParser)
      .then(
        (response: any) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * A method which allows the removal of a documents
   * @param {object} filter
   * The filter to locate a document or documents to be removed
   * @param {string} tenantId
   * Optional tenantId
   * @returns {promise} it will resolve with success message, or fail with error description
   */
  remove(filter: object, tenantId?: string) {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    if (!angular.isObject(filter)) {
      return this.$q.reject('The filter must be of type Object.');
    }

    return this.$http
      .post(
        this.getLocation(tenantSection) + '/remove',
        {
          filter: filter,
          // TODO CORE-440 move error to catch
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then(
        (response: any) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   * Method that saves an existing document to the server.
   * @param {object} document to be saved
   * @param {string} tenantId
   * Optional tenantId
   * @return {Promise}
   * It will return a promise
   */
  save(document: T, tenantId?: string): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .post(
        this.getLocation(tenantSection) + '/save',
        {
          data: document,
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then(
        (response: any) => response.data?.data,
        (error) => this.$q.reject(error.data.message),
      );
  }
}

@Injectable('spynlBase')
export class SpynlBaseDefault extends SpynlBase<Object> {
  constructor(
    $http: ng.IHttpService,
    $q: ng.IQService,
    sentry,
    domain: Domain,
    config: IConfig,
    standardErrorParser: IStandardErrorParser,
  ) {
    'ngInject';

    super($http, $q, sentry, domain, config, standardErrorParser, Object);
  }
}
